import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Button } from '../ui';
import './CharacterForm.css';

/**
 * CharacterForm component
 * Form for creating or editing a character
 *
 * @param {Object} props - Component props
 * @param {Object} props.character - Character object (for editing)
 * @param {Function} props.onSubmit - Callback for form submission
 * @param {Function} props.onCancel - Callback for form cancellation
 * @param {boolean} props.isEditing - Whether we're editing an existing character
 */
const CharacterForm = forwardRef(({
  character,
  onSubmit,
  onCancel,
  isEditing = false
}, ref) => {
  const [name, setName] = useState('');
  const [creature, setCreature] = useState('');
  const [description, setDescription] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form to initial state
  const reset = () => {
    setName('');
    setCreature('');
    setDescription('');
    setPronouns('');
    setHasChanges(false);
    setErrors({});
    setIsSubmitting(false);
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    reset,
    hasChanges
  }));

  // Update form when character changes
  useEffect(() => {
    if (character) {
      setName(character.name);
      setCreature(character.creature);
      setDescription(character.description);
      setPronouns(character.pronouns || '');
      setHasChanges(false);
      setErrors({});
    } else {
      reset();
    }
  }, [character]);

  // Validate a form field
  const validateField = (fieldName, value) => {
    if (!value.trim() && fieldName !== 'pronouns') {
      return 'This field is required';
    }
    if (fieldName === 'name' || fieldName === 'creature') {
      // Only validate length, allow any characters
      if (value.length > 100) {
        return 'Maximum length is 100 characters';
      }
    }
    return '';
  };

  // Handle changes to form fields
  const handleFieldChange = (field, value) => {
    const error = validateField(field, value);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));

    switch (field) {
      case 'name':
        setName(value);
        break;
      case 'creature':
        setCreature(value);
        break;
      case 'description':
        setDescription(value);
        break;
      case 'pronouns':
        setPronouns(value);
        break;
      default:
        console.warn(`Unhandled field type: ${field}`);
        break;
    }
    setHasChanges(true);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    // Validate all fields
    const newErrors = {
      name: validateField('name', name),
      creature: validateField('creature', creature),
      description: validateField('description', description)
    };

    if (Object.values(newErrors).some(error => error)) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      onSubmit(name.trim(), creature.trim(), description.trim(), pronouns);
      setHasChanges(false);
    } catch (error) {
      console.error('Form submission error:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="character-form">
      {hasChanges && (
        <div className="character-form__warning">
          You have unsaved changes
        </div>
      )}
      
      <div className="character-form__field">
        <label className="character-form__label" htmlFor="character-name">
          Name
        </label>
        <input
          id="character-name"
          type="text"
          value={name}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          placeholder="Character name"
          className={`character-form__input ${errors.name ? 'character-form__input--error' : ''} ${isEditing ? 'character-form__input--readonly' : ''}`}
          disabled={isEditing || isSubmitting}
          required
        />
        {errors.name && (
          <div className="character-form__error">{errors.name}</div>
        )}
      </div>
      
      <div className="character-form__field">
        <label className="character-form__label" htmlFor="character-creature">
          Type of Creature
        </label>
        <input
          id="character-creature"
          type="text"
          value={creature}
          onChange={(e) => handleFieldChange('creature', e.target.value)}
          placeholder="Type of creature"
          className={`character-form__input ${errors.creature ? 'character-form__input--error' : ''}`}
          disabled={isSubmitting}
          required
        />
        {errors.creature && (
          <div className="character-form__error">{errors.creature}</div>
        )}
      </div>
      
      <div className="character-form__field">
        <label className="character-form__label" htmlFor="character-pronouns">
          Pronouns (optional)
        </label>
        <select
          id="character-pronouns"
          value={pronouns}
          onChange={(e) => handleFieldChange('pronouns', e.target.value)}
          className="character-form__input"
          disabled={isSubmitting}
        >
          <option value=""></option>
          <option value="she/her">She/Her</option>
          <option value="he/him">He/Him</option>
          <option value="they/them">They/Them</option>
        </select>
      </div>
      
      <div className="character-form__field">
        <label className="character-form__label" htmlFor="character-description">
          Description
        </label>
        <textarea
          id="character-description"
          value={description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          placeholder="Character description"
          className={`character-form__textarea ${errors.description ? 'character-form__textarea--error' : ''}`}
          disabled={isSubmitting}
          rows={4}
          required
        />
        {errors.description && (
          <div className="character-form__error">{errors.description}</div>
        )}
      </div>
      
      <div className="character-form__actions">
        {onCancel && (
          <Button 
            variant="text" 
            onClick={onCancel}
            type="button"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        
        <Button 
          variant="primary" 
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting 
            ? (isEditing ? 'Saving...' : 'Creating...') 
            : (isEditing ? 'Save Changes' : 'Create Character')}
        </Button>
      </div>
    </form>
  );
});

CharacterForm.displayName = 'CharacterForm';

export default CharacterForm;